// SignUp
import React, { useEffect, useState, useRef, useMemo } from "react";
import { signUp, checkEmailExists /* uploadProfilePic */ } from "../utils/botFactoryAPI"; // Import 
import { Footer } from '../components/footer/Footer';
import { OrgSettings } from '../tools/orgSettings';
import { usePreAuthOrg } from '../utils/usePreAuthOrg';
import { PreAuthNotice } from '../components/header/PreAuthNotice';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { setFavicon } from '../utils/favicon';
import { iconSources } from '../utils/orgIcon';
import { ProfilePicturePicker } from '../utils/upload/ProfilePicturePicker';
import { AvatarImage } from '../components/header/AccountAvatar';

/* Device Agent */
//import "./SignInUpPage.scss";
import useDeviceType from '../utils/getAgent';

export const VERIFY_EMAIL_NOTE = 'Check your email to verify your address.';

// The sign-in page for the same agent: /signin with this page's query string (?id= etc.) kept.
const signInUrl = () => (window.location.search ? `/signin${window.location.search}` : '/signin');
export const PASSWORD_MISMATCH_TEXT = 'Passwords do not match';

function SignUpPage() {
  // ...existing state and hooks...
  
  const areRequiredFieldsValid = () => {
    if (!firstname || !lastname || !email || !password || !passwordAgain || !mobilePhone) return false;
    if (emailError) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    if (password.length < 8 || password !== passwordAgain) return false;
    return true;
  };

  const isFormValidForPicUpload = () => areRequiredFieldsValid(); // kept for future use

  const isFormReadyForTerms = () => areRequiredFieldsValid();

  // const [urlInput, setUrlInput] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [signedUpNote, setSignedUpNote] = useState(""); // v2.08: shown after a successful sign-up, never blocking
  const [showVerifyDialog, setShowVerifyDialog] = useState(false); // the verification-email dialog (X, OK, Cancel)
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [email, setEmailState] = useState("");
  const [password, setPasswordState] = useState("");
  const [passwordAgain, setPasswordAgainState] = useState("");
  // Shown as they type: Submit stays disabled on a mismatch, so the submit-time message never appears.
  const passwordMismatch = passwordAgain !== "" && password !== passwordAgain;
  const [salutation, setSalutationState] = useState("");
  
  const [firstname, setFirstnameState] = useState("");
  const [lastname, setLastnameState] = useState("");
  const [username, setUsernameState] = useState("");

  const [emailError, setEmailError] = useState<string | null>(null);

  const [selectedOrg, setSelectedOrgState] = useState<{ name?: string; id?: string }>({
    name: "Cloud Media Works, LLC",
    id: "67e1a7a7ae5516201dce92d2",
  });

  const [emailVerified, emailVerifiedState] = useState(false);
  const [verificationToken, setVerificationTokenState] = useState("");
 
  const [role, setRoleState] = useState<{ type?: string }>({ type: "user" });

  const [mobilePhone, setMobilePhoneState] = useState("");

  const [pic, setPicState] = useState<string | File>('');
  const [logoWide, setLogoWide] = useState(false); // wordmark logo: show whole, not as a cropped circle

  const [terms, setTermsState] = useState("");
  const [isChecked, setIsChecked] = useState(false);
const [isUploadingPic] = useState(false);
   
  interface org {
    id: string;
    orgName: string;
    logo: string;
  }

  const queryParams = new URLSearchParams(window.location.search);
  const botId = queryParams.get("id") || '';


  // [S2-B B2] The bot + its branding from the DB (GET /agent-bootstrap?id=) — was iBot.json +
  // settings.json. Only with an id: /signup without one keeps its generic "Robot Studio" form.
  const pre = usePreAuthOrg(botId || null);
  const record = botId && pre.status === 'ready' ? pre.record : null;

  // The pre-auth projection has no config.title/name (bootstrap allowlist); the page's own
  // precedence below falls through to branding, exactly as it did from settings.json.
  const selectedBot = useMemo(() => {
    if (!record) return undefined;
    const b = record.branding || {};
    return {
      botid: pre.botId, name: b.name, endpoint: record.config?.endpoint, type: record.config?.type,
      header: record.config?.header, thumbnail: b.thumbnail, avatar: b.avatar, favicon: b.favicon,
      subtitle: b.subtitle, containerStyle: b.containerStyle, buttonStyle: b.buttonStyle,
      headerBackColor: b.headerBackColor, promptButtonColor: b.promptButtonColor, fontFace: b.fontFace,
      flyout: b.flyout, footer: b.footer, orgs: [],
    } as any;
  }, [record, pre.botId]);

  const settingsObj = useMemo(() => (record ? pre.org : null), [record, pre.org]);

  
  // Resolve orgObj from orgs or default - TODO: Use the API fo this
  const orgObj: OrgSettings = useMemo(() => {
    if (botId && pre.org && pre.status === 'ready') {
      console.log("🌐 Org resolved from the record for botId:", botId);
      return pre.org;
    }
    const fallbackFlyout = { urls: [] };
    const fallbackFooter = {
      disclaimer: "AI Assistants can make mistakes.",
      copyright: "© 2025",
      backcolor: "#0a2740",
      links: [],
    };
    // Fallback to bot settings
    if (selectedBot) {
      return {
        id: selectedBot?.orgs?.[0]?.id || 'org-default',
        name: selectedBot?.orgs?.[0]?.name || selectedBot.name || 'Org',
        endpoint: selectedBot.endpoint,
        model: selectedBot.model,
        type: (selectedBot.type as any) || 'voice,text',
        logo: selectedBot.thumbnail || selectedBot.avatar || 'assets/app/logo192.png',
        botid: selectedBot.botid,
        favicon: selectedBot.favicon || '/favicon.ico',
        homepage: selectedBot.header?.link || '/',
        title: selectedBot.title || selectedBot.name,
        subtitle: selectedBot.subtitle || '',
        edition: '',
        containerStyle: selectedBot.containerStyle || { backgroundColor: "#0a2740", color: "#e8eef5" },
        buttonStyle: selectedBot.buttonStyle || { backgroundColor: "#004c8c", color: "#ffffff" },
        headerBackColor: selectedBot.headerBackColor || '#0a2740',
        promptButtonColor: selectedBot.promptButtonColor || '#ffffff',
        fontFace: selectedBot.fontFace || 'Arial, sans-serif',
        flyout: selectedBot.flyout || fallbackFlyout,
        footer: selectedBot.footer || selectedBot.settings?.footer || fallbackFooter,
      };
    }
    console.log("⚠️ No botId; defaulting org minimal");
    return {
      id: "org-default",
      name: "Robot Studio",
      endpoint: "",
      model: "",
      type: "voice,text",
      logo: "assets/app/logo192.png",
      botid: "",
      favicon: "/favicon.ico",
      homepage: "/",
      title: "Sign Up",
      subtitle: "",
      edition: "",
      containerStyle: { backgroundColor: "#0a2740", color: "#e8eef5" },
      buttonStyle: { backgroundColor: "#004c8c", color: "#ffffff" },
      headerBackColor: "#0a2740",
      promptButtonColor: "#ffffff",
      fontFace: "Arial, sans-serif",
      flyout: fallbackFlyout,
      footer: fallbackFooter,
    };
  }, [botId, selectedBot]);
  
  const signUpPic = settingsObj?.logo || selectedBot?.thumbnail || selectedBot?.avatar || orgObj?.logo || 'assets/app/logo192.png';
  const signUpTitle = settingsObj?.name || selectedBot?.title || 'Create Account';
  const footerCfg = selectedBot?.footer || selectedBot?.settings?.footer;
  const pageTitle = settingsObj?.name || selectedBot?.title || selectedBot?.name || 'Sign Up';
  const pageBg =
    settingsObj?.containerStyle?.backgroundColor ||
    selectedBot?.containerStyle?.backgroundColor ||
    selectedBot?.theme?.backgroundColor ||
    '#0a2740';
  const pageFg =
    settingsObj?.bodyStyle?.color ||
    selectedBot?.containerStyle?.color ||
    selectedBot?.theme?.foregroundColor ||
    '#e8eef5';
  const buttonBg =
    settingsObj?.buttonStyle?.backgroundColor ||
    selectedBot?.buttonStyle?.backgroundColor ||
    selectedBot?.theme?.buttonColor ||
    '#004c8c';
  const buttonFg =
    settingsObj?.buttonStyle?.color ||
    selectedBot?.buttonStyle?.color ||
    selectedBot?.theme?.buttonTextColor ||
    '#ffffff';
  const formWidth = '100%';
  const formMaxWidth = '320px';
  const avatarSize = 140;

  useEffect(() => {
    if (selectedBot) {
      try {
        localStorage.setItem('SelectedBot', JSON.stringify(selectedBot));
      } catch (e) {
        console.warn('SignUp: failed to persist SelectedBot', e);
      }
    }
    document.title = pageTitle;
    // Favicon: the agent's square icon, else its logo, fitted and never stretched (same as the Header)
    setFavicon(iconSources(settingsObj || selectedBot, ['/favicon.ico']));
  }, [selectedBot, pageTitle, settingsObj]);

  // The chosen picture, shown beside the buttons: a link as it is, an uploaded file through a temporary address.
  const picPreview = useMemo(
    () => (pic instanceof File ? URL.createObjectURL(pic) : typeof pic === 'string' && pic ? pic : undefined),
    [pic],
  );
  useEffect(() => () => { if (picPreview?.startsWith('blob:')) URL.revokeObjectURL(picPreview); }, [picPreview]);

  const emailRef = useRef<HTMLInputElement | null>(null);

  const deviceType = useDeviceType();

  //Check email on blur
  const handleEmailBlur = async () => {
    if (!email) {
      setEmailError(null); // No email entered, no error
      return;
    }
    try {
      const exists = await checkEmailExists(email);
      if (exists) {
        setEmailError("This email is already registered.");
      } else {
        setEmailError(null);
      }
    } catch (error) {
      // Availability check failed (network/backend issue) - do not block
      // sign-up on this; signUp() re-validates email availability server-side.
      console.error("Error checking email existence:", error);
      setEmailError(null);
    }
  };

  // Update Selected org
  useEffect(() => {
    setSelectedOrgState({
      name: orgObj?.name || "Cloud Media Works, LLC",
      id: orgObj?.id || "67e1a7a7ae5516201dce92d2",
    });
  }, [orgObj]);


  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  const handleCheckboxChange = () => {
    // Let the user choose; form validation will still run on submit
    setIsChecked(prev => !prev);
  };

  {/* SIGN UP USER */}
  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      console.log('🔍 Sign-up form submitted');
      setErrorMessage(""); // Clear previous error messages
    
      if (emailError) {
        setErrorMessage(emailError);
        return;
      }
    
      // Validate required fields
      const errors: string[] = [];
      if (!firstname) errors.push("First Name is required");
      if (!lastname) errors.push("Last Name is required");
      if (!email) errors.push("Email is required");
      if (!password) errors.push("Password is required");
      if (!mobilePhone) errors.push("Mobile number is required");
    
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (email && !emailRegex.test(email)) {
        errors.push("Please enter a valid email address");
      }
    
      // Validate password strength
      if (password && password.length < 8) {
        errors.push("Password must be at least 8 characters long");
      }
    
      // Validate password match (blank confirmation counts as a mismatch too)
      if (password && password !== passwordAgain) {
        errors.push("Passwords do not match. Please re-enter");
      }
    
      // Terms & Conditions validation
      if (!isChecked) {
        errors.push("You must agree to the Terms and Conditions");
      }
    
      // If there are errors, display them and stop submission
      if (errors.length > 0) {
        console.log('❌ Validation errors:', errors);
        setErrorMessage(errors.join("\n"));
        return;
      }
    
      console.log('✅ Form validation passed');
      setShowVerifyDialog(true); // OK in that dialog calls confirmSignUp; Cancel and X come back to this form
    };

  {/* OK in the verification dialog: create the account; the account API emails the verification link */}
  const confirmSignUp = async () => {
      setIsSigningUp(true);

      // Picture upload: if the user selected a file, pass it through to
      // signUp(), which uploads it to imageStorage and resolves fileOriginId.
      // If no file was selected, signUp() falls back to the default avatar.
      const userData = {
        email,
        password,
        username: `${firstname} ${lastname}`.trim(),
        salutation: salutation || undefined,
        firstname,
        lastname,
        role:['user'],
        mobile: mobilePhone,
        org: {
          orgId: orgObj!.id,
          orgName: orgObj!.name,
        },
        pic: pic || undefined, // an uploaded file or a link; signUp() stores either the same way Account does
        botId: botId || undefined, // v2.08: the account API emails a verification link only when it has the agent
      };
    
      try {
        console.log('📡 Calling signUp API...');
        const response = await signUp(userData);
        console.log("✅ Sign-up successful:", response);
        // No alert. The note links to sign-in: an unverified account can sign in, and some agents send no email.
        setSignedUpNote(botId ? `✅ Account created. ${VERIFY_EMAIL_NOTE}` : '✅ Account created.');
      } catch (error: any) {
        console.error("❌ Sign-up failed:", error);
        setErrorMessage(error.message || "An error occurred during sign-up. Please try again later.");
      } finally {
        setShowVerifyDialog(false);
        setIsSigningUp(false);
      }
    };


  // [S2-B B2] decision A: an id that does not resolve is a state, never a generic form for it.
  if (botId && pre.status !== 'ready' && pre.status !== 'loading') return <PreAuthNotice status={pre.status} />;

  return (
    <div
      id="pageContainer"
      className={`pageContainer ${deviceType}`}
      style={{
        backgroundColor: pageBg,
        color: pageFg,
        minHeight: '100vh',
        padding: '32px 0 64px',
      }}
    >
      <style>{`
        /*
          src/styles/SignInUpPage.scss (loaded dynamically by useDeviceType()) styles this same page
          with vw-based rules and an always-visible scroller on .signUpContainer. Everything below is
          scoped to this page and marked !important so it wins regardless of load order, without
          editing that shared file.
          Design: the same look as the sign-in page (src/styles/SignInUpPage.scss, .inputGroup,
          .signInButton): one centered column of at most 320px, off-white fields, off-white rounded
          buttons side by side, no card. Never a full-width form.
        */
        html, body {
          width: 100% !important;
          height: auto !important;
          min-height: 100dvh;
          overflow-x: hidden !important;
          overflow-y: auto !important;
        }
        body, html, #root {
          background-color: ${pageBg} !important;
        }
        #pageContainer {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          width: 100% !important;
          min-height: 100dvh !important;
          height: auto !important;
          padding: 24px 16px 40px !important;
          overflow-x: hidden !important;
        }
        #pageContainer * { box-sizing: border-box; }
        #pageContainer a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }

        #signUpContainer, .signUpContainer {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          width: 100% !important;
          max-width: 53vw !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 8px auto 24px !important;
          padding: 0 !important;
          text-align: center !important;
          overflow: visible !important;
        }
        .pageImageContainer, #pageImageContainer {
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
          margin: 0 auto !important;
        }
        #signUpPic {
          width: 96px !important;
          height: 96px !important;
          margin: 0 auto 12px !important;
        }
        /* A wide wordmark logo is shown whole: no circular crop, natural proportions. */
        #signUpPic.signUpPic--wide {
          width: auto !important;
          height: 72px !important;
          max-width: min(280px, 90%) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          object-fit: contain !important;
        }
        .signUpTitle {
          text-align: center !important;
          width: 100% !important;
          margin: 0 auto 18px !important;
          font-size: 1.2em;
          font-weight: 800;
        }

        form {
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
        }
        #signUpContainer form br { display: none !important; }
        .signUpInputGroup {
          width: 100% !important;
          max-width: none !important;
          margin: 0 0 15px !important;
          padding: 0 !important;
          text-align: left !important;
        }
        .signUpInputField {
          width: 100% !important;
          min-width: 0 !important;
          margin: 0 0 4px !important;
          padding: 10px !important;
          font-size: 1em !important;
          border: 1px solid #ccc !important;
          border-radius: 5px !important;
          background-color: #F3F1E8 !important;
          color: #000 !important;
        }
        .signUpInputField:focus {
          outline: 2px solid rgba(255, 255, 255, 0.8) !important;
          outline-offset: 1px;
        }
        .signUpLabels {
          display: block !important;
          margin: 0 !important;
          font-size: 12px !important;
          line-height: 1.3 !important;
          opacity: 0.85;
        }
        .signUpPicGroup {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          text-align: left !important;
        }

        .buttonContainer {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 10px !important;
          width: 100% !important;
          max-width: none !important;
          margin: 12px auto 0 !important;
        }
        .signUpButton, .signInButton {
          flex: 0 0 auto !important;
          min-width: 90px !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 12px 20px !important;
          border: none !important;
          border-radius: 10px !important;
          background-color: #7d7c7b !important;
          color: ${pageBg} !important;
          font-size: 16px !important;
          font-weight: bold !important;
          cursor: pointer !important;
          transition: opacity 0.2s ease-in-out;
        }
        .signUpButton:hover, .signInButton:hover { opacity: 0.8; }
        .signInButton:disabled {
          background-color: #b5a786 !important;
          opacity: 0.6;
          cursor: not-allowed !important;
        }

        .footer-links a { margin: 0 8px; }
      `}</style>
        <div
          id="signUpContainer"
          className="signUpContainer"
          style={{
            width: formWidth,
            maxWidth: formMaxWidth,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          {/* Error Message Display */}
          {errorMessage && <div className="errorStatus">{errorMessage}</div>}
          {signedUpNote && (
            <div
              className="signUpNote"
              role="status"
              style={{ margin: '0 0 12px', padding: '10px 12px', borderRadius: 6, fontSize: '0.95rem',
                background: 'rgba(255, 255, 255, 0.12)', color: pageFg, border: '1px solid rgba(255, 255, 255, 0.35)' }}
            >
              {signedUpNote} You can{' '}
              <a href={signInUrl()} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>sign in</a>
              {' '}now.
            </div>
          )}

          <div id="pageImageContainer" className="pageImageContainer">
            <img
              id="signUpPic"
              className={`pagePic${logoWide ? ' signUpPic--wide' : ''}`}
              src={signUpPic}
              alt="logo"
              onLoad={(e) => {
                const img = e.currentTarget;
                setLogoWide(img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight > 1.3);
              }}
              style={{
                width: avatarSize,
                height: avatarSize,
                borderRadius: '50%',
                objectFit: 'cover',
                boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                margin: '0 auto 24px',
                display: 'block',
              }}
            />
          </div>
            {/* Error Message Display */}
          {errorMessage && <div className="errorStatus" style={{width:"100%"}}>{errorMessage}</div>}
        
          <div className="signUpTitle" style={{ color: pageFg, marginBottom: 16 }}>{signUpTitle}</div>

        <form onSubmit={handleSignUp}>
          {/* Email Input */}
          <div className="signUpInputGroup">
          <input
              type="email"
              className="signUpInputField"
              id="email"
              value={email}
              ref={emailRef}
              onChange={(e) => setEmailState(e.target.value)}
              onBlur={handleEmailBlur}  // <-- add this
              placeholder="Email"
              style={{ backgroundColor: '#fff', color: '#222', border: '1px solid #ccc !important'}}
            />
          {emailError && <div className="errorStatus">{emailError}</div>}
            
            <br/>
            <label className='signUpLabels' htmlFor="email" style={{ color: pageFg }}>
              Enter Email
            </label>
          </div>

          {/* Password Input */}
          <div className="signUpInputGroup">
            <input
              className="signUpInputField"  
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPasswordState(e.target.value)}
              placeholder="Password"
              autoComplete="new-password"
              style={{ backgroundColor: '#fff', color: '#222', border: '1px solid #ccc !important' }}
            />
            
            <br/>
            <label className='signUpLabels' htmlFor="password" style={{ color: pageFg }}>
              Enter Password
            </label>
          </div>

          <div className="signUpInputGroup">
            <input
              className="signUpInputField"
              type="password"
              id="passwordAgain"
              value={passwordAgain}
              onChange={(e) => setPasswordAgainState(e.target.value)}
              placeholder="Enter Password Again"
              autoComplete="new-password"
              aria-invalid={passwordMismatch}
              aria-describedby={passwordMismatch ? "passwordAgainError" : undefined}
              style={{ backgroundColor: '#fff', color: '#222', border: '1px solid #ccc !important' }}
            />
          {passwordMismatch && <div className="errorStatus" id="passwordAgainError" role="alert">{PASSWORD_MISMATCH_TEXT}</div>}
            
            <br/>
            <label className='signUpLabels' htmlFor="passwordAgain" style={{ color: pageFg }}>
              Enter Password Again
            </label>
          </div>

          {/* First Name Input */}
          <div className="signUpInputGroup">
            <input
              className="signUpInputField"
              type="text"
              id="firstname"
              value={firstname}
              onChange={(e) => setFirstnameState(e.target.value)}
              placeholder="First Name"
              style={{ backgroundColor: '#fff', color: '#222', border: '1px solid #ccc !important' }}
            />
            
            <br/>
            <label className='signUpLabels' htmlFor="firstname" style={{ color: pageFg }}>
              Enter First Name
            </label>
          </div>

          {/* Last Name Input */}
          <div className="signUpInputGroup">
            <input
              className="signUpInputField"
              type="text"
              id="lastname"
              value={lastname}
              onChange={(e) => setLastnameState(e.target.value)}
              placeholder="Last Name"
              style={{ backgroundColor: '#fff', color: '#222', border: '1px solid #ccc !important' }}
            />
            
            <br/>
            <label className='signUpLabels' htmlFor="lastname" style={{ color: pageFg }}>
              Enter Last Name
            </label>
          </div>

          {/* Org Select */}
          
          {/* <div className="signUpInputGroup">

            <select
              className="signUpInputField"
              value={selectedOrg}
              onChange={(e) => setSelectedOrgState(e.target.value)}
              style={{ marginRight: '10px', padding: '5px', display: 'none' }}
            >
              <option value="carat-venture-partners" data-id="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxx1">
                Carat Venture Partners
              </option>

              <option value="cloud-media-works" data-id="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxx3">
                Cloud Media Works
              </option>
              <option value="none" data-id="0">
                None
              </option>
            </select>
            
            <br/>
            <div className='signUpLabels' >
              Select Company, [+] to create new
            </div>
            <a className='createOrg' href="/createOrg" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <i className="fas fa-plus" style={{ color: "white", backgroundColor: "#551540", padding: "5px", borderRadius: "50%" }}></i>
              <span style={{ marginLeft: "10px", color: "blue", fontWeight: "bold" }}>&nbsp; </span>
            </a>
          </div>
          */}

          {/* Mobile Phone Input */}
          <div className="signUpInputGroup">
            <input
              className="signUpInputField"
              type="tel"
              id="mobilephone"
              value={mobilePhone}
              onChange={(e) => setMobilePhoneState(e.target.value)}
              placeholder="Mobile Phone"
              style={{ backgroundColor: '#fff', color: '#222',border: '1px solid #ccc !important' }}
            />
            <br/>
            <label className='signUpLabels' htmlFor="mobilephone" style={{ color: pageFg }}>
              Enter Mobile Phone
            </label>
          </div>

          {/* Profile Picture Upload (optional) */}
          <div className="signUpInputGroup signUpPicGroup" style={{ '--ui-button-bg': buttonBg, '--ui-button-fg': buttonFg } as React.CSSProperties}>
            <ProfilePicturePicker
              onChange={(chosen) => setPicState(chosen ?? '')}
              disabled={isUploadingPic}
              hasPicture={pic !== ''}
              showFilePreview={false}
              leading={<AvatarImage src={picPreview} alt="Your picture" size={56} />}
            />
            <label className='signUpLabels' style={{ color: pageFg }}>
              Profile Picture (optional)
            </label>
          </div>
          <br />
          {/* Terms Agreement Checkbox */}
          <div style={{textAlign:"center", width:"100%", color: pageFg}}>
          <label>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              disabled={isUploadingPic}
            />
            I have read and agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>.
          </label>
          </div>
          <br />

          {/* Buttons */}
          <div className="buttonContainer">
          <button className="signUpButton signInColor" type="button" 
              style={{ backgroundColor: buttonBg, color: buttonFg }}
              onClick={() => {
                window.location.href = signInUrl();
              }}>
              Back to Sign In
            </button>
            <button
             className="signInButton signUpColor"
             type="submit"
             disabled={!isChecked || isUploadingPic || (password !== passwordAgain) || !!signedUpNote}
             style={{ backgroundColor: buttonBg, color: buttonFg }}
             >{isUploadingPic ? 'Uploading...' : 'Submit'}</button>
          </div>
        </form>
        </div>
    <ConfirmDialog
      open={showVerifyDialog}
      title="Verify your email"
      message={
        botId
          ? `We will send a verification link to ${email}. Press OK to create your account and send it, or Cancel to go back to the form.`
          : 'Press OK to create your account, or Cancel to go back to the form.'
      }
      busy={isSigningUp}
      onOk={confirmSignUp}
      onCancel={() => setShowVerifyDialog(false)}
      colors={{ background: pageBg, text: pageFg, buttonBackground: buttonBg, buttonText: buttonFg }}
    />
    <Footer footer={footerCfg}/>
  </div>


  );
}

export default SignUpPage;
